import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Quote, Play } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { GALLERY, ALBUMS, VIDEO_TESTIMONIALS, PHOTO_TESTIMONIALS } from "../data/site";

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [album, setAlbum] = useState("All");
  const [video, setVideo] = useState(null);

  const photos = album === "All" ? GALLERY : GALLERY.filter((g) => g.album === album);

  return (
    <div data-testid="gallery-page" className="pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        <Reveal>
          <span className="font-bold text-teal-brand">GALLERY</span>
          <h1 className="font-display font-extrabold text-5xl sm:text-7xl text-navy mt-2 leading-[0.95]">
            Moments from <span className="text-marigold">our classrooms.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-navy/70">
            Years of curiosity, results and celebrations — with the students,
            parents and educators who make The Thinkstitute what it is.
          </p>
        </Reveal>
      </section>

      {/* Album filter */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8" data-testid="album-filter">
        <div className="flex flex-wrap gap-3">
          {ALBUMS.map((a) => (
            <button
              key={a}
              onClick={() => setAlbum(a)}
              data-testid={`album-${a.toLowerCase()}`}
              className={`rounded-full border-2 px-5 py-2.5 font-display font-bold transition-colors ${
                album === a
                  ? "bg-navy text-cream border-navy"
                  : "bg-white text-navy border-navy/15 hover:border-marigold hover:text-marigold"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </section>

      {/* Masonry photo grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="gallery-grid">
        <div key={album} className="columns-1 sm:columns-2 lg:columns-3 gap-5">
          {photos.map((g, i) => (
            <motion.button
              key={g.src}
              onClick={() => setActive(g)}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group relative mb-5 block w-full overflow-hidden rounded-3xl border border-navy/10 break-inside-avoid text-left"
            >
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${g.tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}
              />
              <span className="absolute top-3 left-3 rounded-full bg-white/90 text-navy text-xs font-bold px-3 py-1">{g.album}</span>
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/0 to-navy/0 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="absolute bottom-4 left-4 right-4 text-cream font-semibold text-sm translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                {g.caption}
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Video highlights */}
      <section className="py-24 bg-navy text-cream" data-testid="video-highlights">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-12">
            <span className="font-bold text-marigold">VIDEO HIGHLIGHTS</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">
              Hear it from them.
            </h2>
            <p className="mt-4 text-cream/70">Short clips from our students and parents.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {VIDEO_TESTIMONIALS.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.1}>
                <button
                  onClick={() => setVideo(v)}
                  data-testid={`video-item-${i}`}
                  className="group relative w-full overflow-hidden rounded-3xl text-left aspect-[4/5]"
                >
                  <img src={v.poster} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-navy/0" />
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-16 h-16 rounded-full bg-marigold text-white shadow-xl group-hover:scale-110 transition-transform">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-cream/90 font-semibold">{v.line}</p>
                    <p className="mt-2 font-display font-bold text-lg">{v.name}</p>
                    <p className="text-sm text-cream/60">{v.role}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Photo testimonials */}
      <section className="py-24 bg-indigo-brand text-cream" data-testid="photo-testimonials">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="font-bold text-marigold">IN THEIR WORDS</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">
              Faces of The Thinkstitute.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PHOTO_TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 0.1}>
                <div className="h-full rounded-3xl bg-cream/10 border border-cream/15 p-7 flex flex-col" data-testid={`photo-testimonial-${i}`}>
                  <Quote className="text-teal-brand" size={30} />
                  <p className="mt-4 text-cream/90 flex-1 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={t.photo} alt={t.name} loading="lazy" className="w-12 h-12 rounded-full object-cover border-2 border-marigold" />
                    <div>
                      <p className="font-display font-bold">{t.name}</p>
                      <p className="text-sm text-cream/60">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy">
              Want your child in the next photo?
            </h2>
            <Link
              to="/admissions"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-marigold text-white font-bold text-lg px-8 py-4 shadow-[0_6px_0_0_#c85c14] hover:translate-y-0.5 hover:shadow-[0_3px_0_0_#c85c14] transition-all"
              data-testid="gallery-cta"
            >
              Book a Free Consultation <ArrowRight size={20} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Image lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[70] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-5"
            data-testid="gallery-lightbox"
          >
            <button onClick={() => setActive(null)} className="absolute top-5 right-5 text-cream hover:text-marigold" aria-label="Close" data-testid="lightbox-close">
              <X size={32} />
            </button>
            <motion.figure
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-3xl w-full"
            >
              <img src={active.src} alt={active.caption} className="w-full rounded-2xl object-contain max-h-[80vh]" />
              <figcaption className="mt-3 text-center text-cream/80 font-semibold">{active.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video modal */}
      <AnimatePresence>
        {video && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideo(null)}
            className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-5"
            data-testid="video-modal"
          >
            <button onClick={() => setVideo(null)} className="absolute top-5 right-5 text-cream hover:text-marigold" aria-label="Close" data-testid="video-close">
              <X size={32} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-3xl w-full"
            >
              <video src={video.video} controls autoPlay playsInline className="w-full rounded-2xl bg-black max-h-[75vh]" />
              <p className="mt-3 text-center text-cream/80"><span className="font-display font-bold text-cream">{video.name}</span> · {video.role}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
